import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import BrandMark from '../components/BrandMark';

interface PublicProfile {
  id: string;
  full_name: string;
  role: string;
  public_handle: string;
  public_bio: string;
}

export default function PublicProfilePage() {
  const { handle } = useParams();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!handle) return;
    (async () => {
      const { data: p } = await supabase
        .rpc('get_public_profile', { p_handle: handle })
        .maybeSingle();
      if (p) setProfile(p);
      setLoading(false);
    })();
  }, [handle]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-warm-50 text-ink-500 text-sm">Loading...</div>;
  }
  if (!profile) {
    return (
      <div className="min-h-screen grid place-items-center bg-warm-50 px-6">
        <div className="text-center">
          <h1 className="font-display text-3xl mb-2">Profile not found</h1>
          <p className="text-ink-600 mb-5">This handle is unclaimed or private.</p>
          <Link to="/" className="btn-primary">Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50">
      <header className="bg-white border-b border-ink-100">
        <div className="container-app py-4 flex items-center justify-between">
          <Link to="/" aria-label="PromptGPT home"><BrandMark tone="dark" size={24} /></Link>
          <Link to="/login" className="btn-secondary text-sm">Make your own</Link>
        </div>
      </header>
      <div className="container-app py-12">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-2">Public notebook</div>
          <h1 className="font-display text-5xl mb-2">{profile.full_name || `@${profile.public_handle}`}</h1>
          {profile.role && <div className="text-accent-700 font-medium mb-3">{profile.role}</div>}
          {profile.public_bio && <p className="text-ink-600 leading-relaxed">{profile.public_bio}</p>}
        </div>

        <div className="card p-12 text-center max-w-2xl">
          <BookOpen className="w-10 h-10 text-ink-300 mx-auto mb-4" />
          <h3 className="font-display text-2xl mb-2">A reflection notebook in progress.</h3>
          <p className="text-ink-600 leading-relaxed">
            {profile.full_name || `@${profile.public_handle}`} is learning AI on PromptGPT. Reflections are private by default.
          </p>
        </div>

        <div className="text-center mt-16">
          <Link to="/login?mode=signup" className="inline-flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-ink-900">
            Start your own learning notebook on PromptGPT <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
